// api2's /districts family.
//
// Three endpoints, three jobs:
//   /districts?address=            address in, districts out (LocationPicker)
//   /districts/officials?state=    districts in, officeholders out (the page)
//   /districts/state/{state}       every district in a state (manual picker)
//
// Reading api2 is defensive on purpose. The responses are untyped, so every
// field is read through `pick`, which takes the first key that actually carries
// a value. Key names marked GUESS have not been confirmed against a live
// response -- if a field renders empty, those are the lines to check first.
//
// Officials come out in the same shape officialsDirectory produces, so both
// sources render through the same card and MemberDetailPage keeps receiving the
// fields it reads out of router state. Once the two normalizers are confirmed
// identical they should be consolidated into one module.

const API2_BASE = 'https://api2.govglance.org';

// ─── Response reading ─────────────────────────────────────────────────────────

const pick = (source, ...keys) => {
  if (!source || typeof source !== 'object') return null;
  for (const key of keys) {
    const value = source[key];
    if (value !== undefined && value !== null && value !== '') return value;
  }
  return null;
};

// api2 returns a bare object where a single result exists and a list where
// several do. Everything downstream wants a list.
const toArray = (value) => {
  if (Array.isArray(value)) return value;
  if (value && typeof value === 'object') return [value];
  return [];
};

const request = async (path, params = {}, { signal } = {}) => {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      query.append(key, String(value));
    }
  });

  const suffix = query.toString() ? `?${query}` : '';
  const response = await fetch(`${API2_BASE}${path}${suffix}`, { signal });

  if (!response.ok) {
    const error = new Error(`api2 ${path} responded ${response.status}`);
    error.status = response.status;
    throw error;
  }

  return response.json();
};

// ─── Seat groups ──────────────────────────────────────────────────────────────
// The buckets the page renders under. `statewide` exists because api2 now
// separates U.S. Senators and the governor from district-drawn seats -- they
// have no district, and treating a missing district as a missing seat is the
// bug the endpoint was shaped to prevent.
//
// County and city are separate groups rather than one `local`. The directory
// already returns them in separate buckets carrying separate levels, and they
// are separate governments: a county commissioner and a city councillor are not
// two views of the same seat, and a heading that says "City and county" over
// both of them is the only place in the app where two governments share one.
//
// `local` survives for the directory's other_local bucket -- special districts,
// tribal governments -- which is genuinely neither.

export const SEAT_GROUP = {
  STATEWIDE: 'statewide',
  CONGRESSIONAL: 'congressional',
  UPPER: 'upper',
  LOWER: 'lower',
  COUNTY: 'county',
  CITY: 'city',
  LOCAL: 'local',
};

export const SEAT_GROUP_RANK = {
  [SEAT_GROUP.STATEWIDE]: 0,
  [SEAT_GROUP.CONGRESSIONAL]: 1,
  [SEAT_GROUP.UPPER]: 2,
  [SEAT_GROUP.LOWER]: 3,
  [SEAT_GROUP.COUNTY]: 4,
  [SEAT_GROUP.CITY]: 5,
  [SEAT_GROUP.LOCAL]: 6,
};

export const DISTRICT_GROUPS = new Set([
  SEAT_GROUP.CONGRESSIONAL,
  SEAT_GROUP.UPPER,
  SEAT_GROUP.LOWER,
]);

// Groups that render as one section rather than one section per district.
// Nobody holds "a district" of a county commission in a way this page can
// resolve, so these are grouped by government instead.
export const SINGLE_SECTION_GROUPS = new Set([
  SEAT_GROUP.STATEWIDE,
  SEAT_GROUP.COUNTY,
  SEAT_GROUP.CITY,
  SEAT_GROUP.LOCAL,
]);

// Every group that is not federal or state. The Representatives page's Local
// tab covers all of them together, which is also what api2 means by level=local
// -- county and city as one answer.
export const LOCAL_GROUPS = new Set([
  SEAT_GROUP.COUNTY,
  SEAT_GROUP.CITY,
  SEAT_GROUP.LOCAL,
]);

// Maps the level/chamber pair officialsDirectory returns onto the same buckets,
// so directory rows and /districts rows can sit in one list.
export const groupForLevelChamber = (level, chamber) => {
  const levelKey = String(level || '').toLowerCase();
  const chamberKey = String(chamber || '').toLowerCase();

  if (levelKey === 'federal') {
    return chamberKey === 'senate' ? SEAT_GROUP.STATEWIDE : SEAT_GROUP.CONGRESSIONAL;
  }

  if (levelKey === 'state') {
    if (chamberKey === 'executive') return SEAT_GROUP.STATEWIDE;
    if (chamberKey === 'upper' || chamberKey === 'senate') return SEAT_GROUP.UPPER;
    if (chamberKey === 'lower' || chamberKey === 'house' || chamberKey === 'assembly') {
      return SEAT_GROUP.LOWER;
    }
    // A state row with an unrecognized chamber is still a state row. Sorting it
    // under upper keeps it visible rather than dropping it into Local, where a
    // user filtering by State would never see it.
    return SEAT_GROUP.UPPER;
  }

  if (levelKey === 'county') return SEAT_GROUP.COUNTY;
  if (levelKey === 'city' || levelKey === 'place' || levelKey === 'municipal') {
    return SEAT_GROUP.CITY;
  }

  // other_local, and anything a future bucket adds. Still rendered, still under
  // the Local tab, just not claimed as a county or a city when it is neither.
  return SEAT_GROUP.LOCAL;
};

// ─── Level and chamber ────────────────────────────────────────────────────────
// These two fields are load-bearing beyond display: RepresentativesPage opens a
// detail page only when level is 'federal', and picks the senator route over the
// representative route only when chamber is 'senate'. A row that arrives with an
// unexpected value in either field silently loses its link.
//
// So the seat group wins wherever it settles the question. The group comes from
// the response's own structure -- districts.federal.congressional is the U.S.
// House by definition -- which is a firmer signal than a free-text level field.
// Only `statewide` is genuinely mixed (federal senators alongside a state
// governor), and only there is the payload's own wording consulted.

const levelForOfficial = (raw, group) => {
  if (group === SEAT_GROUP.CONGRESSIONAL) return 'federal';
  if (group === SEAT_GROUP.UPPER || group === SEAT_GROUP.LOWER) return 'state';

  if (group === SEAT_GROUP.STATEWIDE) {
    const declared = pick(raw, 'level');
    if (declared === 'federal' || declared === 'state') return declared;

    const office = String(pick(raw, 'office', 'office_name', 'title', 'role') || '').toLowerCase();
    if (office.includes('senator') || office.includes('u.s. senate')) return 'federal';
    return 'state';
  }

  // county and city are levels api2 uses verbatim, so a row that arrived in one
  // of those groups keeps saying so. The Local tab tests for "not federal, not
  // state", which all three satisfy.
  if (group === SEAT_GROUP.COUNTY) return pick(raw, 'level') || 'county';
  if (group === SEAT_GROUP.CITY) return pick(raw, 'level') || 'city';

  return pick(raw, 'level') || 'local';
};

const chamberForOfficial = (raw, group, context) => {
  // congressional is the U.S. House and nothing else, so this is not inferred.
  if (group === SEAT_GROUP.CONGRESSIONAL) return 'house';

  const declared = pick(raw, 'chamber');
  if (declared) return String(declared).toLowerCase();

  if (group === SEAT_GROUP.STATEWIDE) {
    const office = String(pick(raw, 'office', 'office_name', 'title', 'role') || '').toLowerCase();
    if (office.includes('senator')) return 'senate';
    if (office.includes('governor')) return 'executive';
  }

  return context.chamberKey || '';
};

// ─── Officials ────────────────────────────────────────────────────────────────

const NAME_SUFFIXES = new Set(['jr', 'sr', 'ii', 'iii', 'iv', 'v']);
const NAME_HONORIFICS = new Set([
  'rep', 'representative', 'sen', 'senator', 'gov', 'governor',
  'hon', 'honorable', 'mr', 'mrs', 'ms', 'dr', 'the',
]);

const nameTokens = (name) =>
  String(name || '')
    .toLowerCase()
    .replace(/[^a-z\s]/g, ' ')
    .split(/\s+/)
    .filter(Boolean)
    .filter((token) => !NAME_HONORIFICS.has(token) && !NAME_SUFFIXES.has(token));

const sortNameFor = (name) => {
  const tokens = nameTokens(name);
  return tokens.length ? tokens[tokens.length - 1] : '';
};

// First and last name only. The two sources punctuate and title differently --
// "Rep. Jane Q. Doe" against "Jane Doe" -- and middle initials are the least
// reliable part of either.
export const nameMatchKey = (name) => {
  const tokens = nameTokens(name);
  if (!tokens.length) return '';
  return `${tokens[0]}|${tokens[tokens.length - 1]}`;
};

const PARTY_ABBR = {
  democratic: 'D',
  democrat: 'D',
  republican: 'R',
  independent: 'I',
  libertarian: 'L',
  green: 'G',
  nonpartisan: 'N',
};

const partyAbbrFor = (party) => {
  const trimmed = String(party || '').trim();
  if (!trimmed) return '';
  if (trimmed.length <= 2) return trimmed.toUpperCase();
  return PARTY_ABBR[trimmed.toLowerCase()] || trimmed.charAt(0).toUpperCase();
};

// term_start is a display string on federal rows and null everywhere else, so
// pull a year out of it if one is in there and give up quietly otherwise.
const yearFrom = (value) => {
  const match = String(value || '').match(/\b(1[89]\d{2}|20\d{2})\b/);
  return match ? Number(match[0]) : null;
};

// Action labels carry a key rather than English text: this module has no `t`,
// and the page should not be guessing which strings need translating. Rows that
// arrive with their own label from api2 keep it verbatim.
const actionsFor = (raw) => {
  const declared = toArray(raw?.actions)
    .map((action) => ({
      label: pick(action, 'label', 'title', 'name') || '',
      url: pick(action, 'url', 'href'),
      labelKey: null,
    }))
    .filter((action) => action.url && action.label);

  if (declared.length) return declared;

  const derived = [];
  const website = pick(raw, 'website', 'url', 'homepage', 'official_url');
  if (website) {
    derived.push({ label: 'Official website', labelKey: 'common:officialWebsite', url: website });
  }

  const contact = pick(raw, 'contact_form', 'contact_url', 'contact_page'); // GUESS
  if (contact) {
    derived.push({ label: 'Contact form', labelKey: 'common:contactForm', url: contact });
  }

  // Every local row carries the government page it was read from, and about one
  // in a thousand is an artifact rather than a person -- a name field that
  // picked up "Send Email" from a link, or a truncated surname. This is how a
  // reader checks one for themselves, so it is offered on local rows only,
  // where the risk actually lives.
  const source = pick(raw, 'source_url');
  if (source && !derived.some((action) => action.url === source)) {
    derived.push({ label: 'Source', labelKey: 'common:sourcePage', url: source });
  }

  return derived;
};

// Every spelling of the bioguide id seen across the two api2 surfaces. This is
// the field the detail route is built from, so it is worth casting a wide net.
const bioGuideFrom = (raw) =>
  pick(raw, 'bioguide_id', 'bio_guide_id', 'bioGuideId', 'bioguideId', 'bioguide');

const keyFor = (official) =>
  official.bioGuideId
    ? `bg:${official.bioGuideId}`
    : `${official.group}:${official.chamber}:${official.district}:${official.sortName}:${official.name}`;

export const normalizeOfficial = (raw, context = {}) => {
  if (!raw || typeof raw !== 'object') return null;

  const name = pick(raw, 'name', 'full_name', 'display_name', 'official_name');
  if (!name) return null;

  const group = context.group || SEAT_GROUP.LOCAL;
  const party = pick(raw, 'party', 'party_name') || '';

  const district = pick(raw, 'district', 'district_number', 'district_label');
  const resolvedDistrict = district ?? context.district ?? '';

  const official = {
    name,
    sortName: sortNameFor(name),
    party,
    partyAbbr: partyAbbrFor(party),

    office: pick(raw, 'office', 'office_name', 'title', 'role') || context.officeName || '',
    level: levelForOfficial(raw, group),
    chamber: chamberForOfficial(raw, group, context),
    group,

    district: resolvedDistrict === null ? '' : String(resolvedDistrict),
    districtLabel: context.districtLabel || null,
    chamberName: context.chamberName || null,

    jurisdictionName:
      pick(raw, 'jurisdiction_name', 'jurisdiction', 'state_name') || context.jurisdictionName || '',

    phone: pick(raw, 'phone', 'phone_number', 'office_phone'),
    email: pick(raw, 'email', 'email_address'),
    website: pick(raw, 'website', 'url', 'homepage', 'official_url'),
    officeAddress: pick(raw, 'office_address', 'address', 'mailing_address'),
    photoUrl: pick(raw, 'photo_url', 'image', 'image_url', 'photo'),

    bioGuideId: bioGuideFrom(raw),
    termStartYear: yearFrom(pick(raw, 'term_start', 'term_start_year', 'start_year')),
    jobDescription: pick(raw, 'job_description', 'description', 'office_description'),

    actions: actionsFor(raw),

    // Preserves the server's ordering. api2 orders districts naturally -- 2
    // before 10, Idaho's 27 before 27A, New Hampshire's Belknap 1 before
    // Carroll 1 -- and no client-side parse of the label reproduces that, so
    // the order is carried rather than re-derived.
    order: context.order ?? 0,
  };

  // bioguide where one exists, otherwise the seat plus the name -- stable
  // across refetches, which matters because expandedKey holds one of these.
  official.key = keyFor(official);

  return official;
};

// ─── Merging the two sources ──────────────────────────────────────────────────
// /districts/officials owns the district seats, but the directory rows for those
// same seats are not worthless: they carry the bioguide id, the portrait and the
// office address that MemberDetailPage needs, and the districts payload does not
// always include them. Dropping those rows outright is what breaks the link
// through to a House member's detail page.
//
// So a district row is filled in from its matching directory row before the
// duplicate is discarded. The district row wins on everything it actually has;
// the directory only supplies what is missing.

const BACKFILL_FIELDS = [
  'bioGuideId',
  'photoUrl',
  'officeAddress',
  'phone',
  'email',
  'website',
  'termStartYear',
  'jobDescription',
  'office',
  'party',
  'partyAbbr',
  'jurisdictionName',
];

const isEmpty = (value) => value === null || value === undefined || value === '';

export const backfillOfficial = (target, source) => {
  if (!source) return target;

  const merged = { ...target };

  BACKFILL_FIELDS.forEach((field) => {
    if (isEmpty(merged[field]) && !isEmpty(source[field])) {
      merged[field] = source[field];
    }
  });

  if (source.actions?.length) {
    const seen = new Set(merged.actions.map((action) => action.url));
    merged.actions = merged.actions.concat(
      source.actions.filter((action) => !seen.has(action.url))
    );
  }

  // The key is derived from the bioguide id, so acquiring one has to rebuild it
  // or expandedKey and the card key would disagree after a refetch.
  if (isEmpty(target.bioGuideId) && !isEmpty(merged.bioGuideId)) {
    merged.key = keyFor(merged);
  }

  return merged;
};

export const mergeOfficialSources = (districtOfficials, directoryOfficials) => {
  if (!districtOfficials.length) return directoryOfficials;

  // Keyed by group as well as name: a state representative and a U.S.
  // representative can share a name, and filling one from the other would put a
  // legislator's bioguide id on the wrong person.
  const byName = new Map();
  directoryOfficials.forEach((official) => {
    const key = `${official.group}|${nameMatchKey(official.name)}`;
    if (key && !byName.has(key)) byName.set(key, official);
  });

  const enriched = districtOfficials.map((official) =>
    backfillOfficial(official, byName.get(`${official.group}|${nameMatchKey(official.name)}`))
  );

  const remainder = directoryOfficials.filter(
    (official) => !DISTRICT_GROUPS.has(official.group)
  );

  return enriched.concat(remainder);
};

// ─── Seats ────────────────────────────────────────────────────────────────────
// A seat is a district (or the statewide office) plus whoever holds it. Seats
// are what /districts returns and what the page renders sections from.

const officialsWithin = (raw) =>
  toArray(pick(raw, 'officials', 'members', 'representatives', 'officeholders'));

export const normalizeSeat = (raw, context = {}) => {
  if (!raw || typeof raw !== 'object') return null;

  const group = context.group || SEAT_GROUP.LOCAL;
  const district = pick(raw, 'district', 'district_number', 'number', 'district_id');

  // The label the state itself uses -- "Assembly District 12", "House of
  // Delegates 4A". Rendered verbatim rather than composed from a number, since
  // composing it is how you end up calling a Delegate a Representative.
  //
  // district_name first, confirmed from a live response. `label` also exists
  // and is the generic name for the whole layer ("U.S. House district"), so
  // reading it first put the same string on every district in the state and
  // dropped the one number the reader was looking for.
  const districtLabel = pick(raw, 'district_name', 'label', 'district_label', 'name', 'title');

  const chamberName = pick(raw, 'body', 'chamber_name', 'chamber_label', 'chamber_title');
  const chamberKey = context.chamberKey || pick(raw, 'chamber') || null;

  const seatContext = {
    group,
    chamberKey,
    chamberName: chamberName || context.chamberName || null,
    district,
    districtLabel,
    jurisdictionName: context.jurisdictionName,
    officeName: context.officeName,
    order: context.order ?? 0,
  };

  const officials = officialsWithin(raw)
    .map((official, index) => normalizeOfficial(official, { ...seatContext, order: index }))
    .filter(Boolean);

  return {
    key: `${group}:${chamberKey || ''}:${district ?? districtLabel ?? 'seat'}`,
    group,
    chamberKey,
    chamberName: seatContext.chamberName,
    district: district === null || district === undefined ? '' : String(district),
    districtLabel,
    geoid: pick(raw, 'geoid', 'census_geoid', 'district_geoid'),
    officialsUrl: pick(raw, 'officials_url', 'officialsUrl'),
    officials,
    // A seat that matched no officeholder is reported as unfilled rather than
    // 404'd, because the seat may genuinely be vacant. Say vacant; do not drop
    // the row, or the absence reads as a bug in the app.
    vacant: officials.length === 0,
    order: context.order ?? 0,
  };
};

// Handles the three shapes a seat container has been seen in: a list of seats,
// a single seat object, or an object keyed by office ({ senate: [...],
// governor: {...} }) -- which is what `statewide` looks like when grouped.
const collectSeats = (value, context) => {
  if (!value) return [];

  if (Array.isArray(value)) {
    return value
      .map((entry, index) => normalizeSeat(entry, { ...context, order: index }))
      .filter(Boolean);
  }

  if (typeof value !== 'object') return [];

  const looksLikeSeat =
    'district' in value ||
    'officials' in value ||
    'members' in value ||
    'label' in value ||
    'geoid' in value;

  if (looksLikeSeat) {
    const seat = normalizeSeat(value, context);
    return seat ? [seat] : [];
  }

  return Object.entries(value).flatMap(([officeKey, entry], index) =>
    collectSeats(entry, { ...context, officeName: context.officeName || officeKey, order: index })
  );
};

// ─── /districts?address= ──────────────────────────────────────────────────────

const normalizeAddressPayload = (payload) => {
  const districts = payload?.districts || payload || {};
  const federal = districts.federal || {};
  const state = districts.state || {};
  // Confirmed against a live response: the resolved place is `address`, and it
  // carries matched_address, state_abbr, city, county_name, county_geoid and
  // place_geoid. The other three keys were guesses and all three missed, so
  // `source` fell through to the whole payload, state_abbr came back null, and
  // fetchAddressDistricts threw on a 200 -- which the picker reported as an
  // address that could not be matched.
  const source =
    payload?.address || payload?.location || payload?.matched || payload?.resolved || payload || {};

  const stateAbbr = pick(source, 'state', 'state_abbr', 'state_code');
  const context = { jurisdictionName: pick(source, 'state_name') || stateAbbr };

  const congressional =
    collectSeats(federal.congressional, {
      ...context,
      group: SEAT_GROUP.CONGRESSIONAL,
      chamberKey: 'house',
    })[0] || null;

  const upper =
    collectSeats(state.upper, {
      ...context,
      group: SEAT_GROUP.UPPER,
      chamberKey: 'upper',
    })[0] || null;

  const lower =
    collectSeats(state.lower, {
      ...context,
      group: SEAT_GROUP.LOWER,
      chamberKey: 'lower',
    })[0] || null;

  const statewide = collectSeats(districts.statewide, {
    ...context,
    group: SEAT_GROUP.STATEWIDE,
  });

  return {
    matched: {
      address: pick(source, 'matched_address', 'address', 'input_address'),
      city: pick(source, 'city', 'place_name'),
      county: pick(source, 'county', 'county_name'),
      countyGeoid: pick(source, 'county_geoid'),
      stateAbbr,
      geoid: pick(source, 'place_geoid', 'geoid'),
    },
    congressional,
    upper,
    lower,
    statewide,
  };
};

// include_officials defaults to false here even though the endpoint defaults to
// true: the picker only needs to prove the address matched, and pulling every
// officeholder to render a confirmation line is wasted payload.
export const fetchAddressDistricts = async (address, { signal, includeOfficials = false } = {}) => {
  const payload = await request(
    '/districts',
    { address, include_officials: includeOfficials ? 'true' : 'false' },
    { signal }
  );

  const result = normalizeAddressPayload(payload);
  if (!result.matched.stateAbbr) {
    // The request succeeded and the reading failed, which are different
    // problems with different fixes. The payload rides along on the error so
    // the caller can log what actually came back rather than guessing at it.
    const error = new Error('no state resolved');
    error.payload = payload;
    throw error;
  }
  return result;
};

// ─── /districts/officials?state= ──────────────────────────────────────────────

const REQUESTED_GROUPS = [
  { param: 'congressional', group: SEAT_GROUP.CONGRESSIONAL, chamberKey: 'house' },
  { param: 'state_upper', group: SEAT_GROUP.UPPER, chamberKey: 'upper' },
  { param: 'state_lower', group: SEAT_GROUP.LOWER, chamberKey: 'lower' },
];

// The results list has been seen holding both seat objects and bare officials.
// Seats carry an officials array; anything else is an official that has to be
// placed by its own level/chamber.
const seatsFromResults = (results, context) => {
  const entries = toArray(results);
  const seats = [];
  const loose = [];

  entries.forEach((entry, index) => {
    if (officialsWithin(entry).length || 'officials_url' in (entry || {})) {
      const seat = normalizeSeat(entry, { ...context, order: index });
      if (seat) seats.push(seat);
      return;
    }
    loose.push({ entry, index });
  });

  // Bare officials get folded into a seat per group/district pair so the page
  // still has something to render a heading from.
  const bySeat = new Map();
  loose.forEach(({ entry, index }) => {
    const group = groupForLevelChamber(pick(entry, 'level'), pick(entry, 'chamber'));
    const district = pick(entry, 'district', 'district_number') ?? '';
    const seatKey = `${group}:${district}`;

    const official = normalizeOfficial(entry, { ...context, group, district, order: index });
    if (!official) return;

    if (!bySeat.has(seatKey)) {
      bySeat.set(seatKey, {
        key: seatKey,
        group,
        chamberKey: official.chamber,
        chamberName: null,
        district: String(district),
        districtLabel: null,
        geoid: null,
        officialsUrl: null,
        officials: [],
        vacant: false,
        order: index,
      });
    }
    bySeat.get(seatKey).officials.push(official);
  });

  return seats.concat([...bySeat.values()]);
};

export const fetchOfficialsForDistricts = async (
  { stateAbbr, congressional, stateUpper, stateLower },
  { signal } = {}
) => {
  if (!stateAbbr) throw new Error('state is required');
  if (!congressional && !stateUpper && !stateLower) {
    throw new Error('at least one district is required');
  }

  const payload = await request(
    '/districts/officials',
    {
      state: stateAbbr,
      congressional,
      state_upper: stateUpper,
      state_lower: stateLower,
    },
    { signal }
  );

  const context = { jurisdictionName: stateAbbr };
  const seats = seatsFromResults(pick(payload, 'results', 'officials', 'seats'), context);

  // A district that reads fine but matches nobody comes back here rather than
  // as a 404. Rendered as a vacant seat so the user sees "no one holds this"
  // instead of a section that silently isn't there.
  const unfilled = toArray(pick(payload, 'unfilled_seats', 'unfilledSeats')).map((entry, index) => {
    const requested = REQUESTED_GROUPS.find(
      (candidate) => candidate.chamberKey === pick(entry, 'chamber')
    );
    return {
      ...normalizeSeat(entry, {
        ...context,
        group:
          requested?.group || groupForLevelChamber(pick(entry, 'level'), pick(entry, 'chamber')),
        chamberKey: requested?.chamberKey || pick(entry, 'chamber'),
        order: index,
      }),
      vacant: true,
    };
  });

  return {
    seats,
    unfilledSeats: unfilled,
    // Populated when a district was given with no chamber and matched in both.
    note: pick(payload, 'note', 'notes', 'message'),
  };
};

// ─── /districts/state/{state} ─────────────────────────────────────────────────
// Feeds the manual district picker. The summary payload (include_officials
// omitted) is what the picker wants: each district already carries the member
// name and party, which is enough to confirm a pick without a second request.

const CHAMBER_KEYS = {
  upper: 'upper',
  senate: 'upper',
  lower: 'lower',
  house: 'lower',
  assembly: 'lower',
  legislature: 'upper', // Nebraska is unicameral; its one chamber sits in upper.
};

const chamberKeyFor = (raw) => {
  const declared = String(pick(raw, 'chamber', 'chamber_key', 'type') || '').toLowerCase();
  if (CHAMBER_KEYS[declared]) return CHAMBER_KEYS[declared];

  const name = String(pick(raw, 'name', 'chamber_name', 'label') || '').toLowerCase();
  const match = Object.keys(CHAMBER_KEYS).find((token) => name.includes(token));
  return match ? CHAMBER_KEYS[match] : 'upper';
};

// A district entry in this payload is a summary, not a full seat: name, party
// and office rather than the whole official record.
const normalizeDistrictOption = (raw, index) => {
  if (!raw || typeof raw !== 'object') return null;

  const district = pick(raw, 'district', 'district_number', 'number', 'id');
  const label = pick(raw, 'label', 'district_label', 'name', 'title');
  if (district === null && !label) return null;

  const members = toArray(pick(raw, 'members', 'officials', 'member', 'official'))
    .map((member) => ({
      name: pick(member, 'name', 'full_name', 'display_name'),
      party: pick(member, 'party', 'party_name') || '',
      office: pick(member, 'office', 'office_name', 'title'),
    }))
    .filter((member) => member.name);

  return {
    district: district === null || district === undefined ? String(label) : String(district),
    label: label || String(district),
    geoid: pick(raw, 'geoid', 'census_geoid'),
    officialsUrl: pick(raw, 'officials_url', 'officialsUrl'),
    members,
    // Position in the response. The list is already in natural order, so the
    // picker renders it as received and never re-sorts.
    order: index,
  };
};

export const fetchStateDistricts = async (stateAbbr, { signal } = {}) => {
  if (!stateAbbr) throw new Error('state is required');

  const payload = await request(
    `/districts/state/${encodeURIComponent(String(stateAbbr).toUpperCase())}`,
    {},
    { signal }
  );

  const federal = payload?.federal || {};
  const legislature = payload?.state_legislature || payload?.stateLegislature || {};

  const congressional = toArray(federal?.congressional?.districts || federal?.congressional)
    .map(normalizeDistrictOption)
    .filter(Boolean);

  const chambers = toArray(legislature?.chambers)
    .map((chamber) => ({
      key: chamberKeyFor(chamber),
      // The state's own word for the chamber. Nebraska says Legislature, not
      // Senate; Virginia says House of Delegates.
      name: pick(chamber, 'name', 'chamber_name', 'label', 'title') || '',
      districts: toArray(pick(chamber, 'districts', 'district_list'))
        .map(normalizeDistrictOption)
        .filter(Boolean),
    }))
    .filter((chamber) => chamber.districts.length);

  return {
    stateAbbr: pick(payload, 'state', 'state_abbr') || String(stateAbbr).toUpperCase(),
    stateName: pick(payload, 'state_name', 'name'),
    congressional,
    chambers,
  };
};

// ─── Grouping for render ──────────────────────────────────────────────────────
// Turns a flat official list into ordered sections. Officials sourced from
// /districts already know their group; directory rows are placed by
// groupForLevelChamber above.

export const buildSeatSections = (officials, seats = []) => {
  const sections = new Map();

  const sectionFor = (official) => {
    const group = official.group || groupForLevelChamber(official.level, official.chamber);

    // District groups get one section per district, since two people never
    // share a seat. Everything else is one section per government -- and the
    // government is part of the key, so a county commission and a city council
    // do not collapse into each other, and neither do two counties.
    const key = SINGLE_SECTION_GROUPS.has(group)
      ? `${group}:${official.jurisdictionName || ''}`
      : `${group}:${official.district || ''}`;

    if (!sections.has(key)) {
      sections.set(key, {
        key,
        group,
        district: official.district || '',
        districtLabel: official.districtLabel || null,
        chamberName: official.chamberName || null,
        // Carried so the page can head a county section with the county's own
        // name rather than the word "County".
        jurisdictionName: official.jurisdictionName || null,
        officials: [],
        vacant: false,
        order: official.order ?? 0,
      });
    }
    return sections.get(key);
  };

  officials.forEach((official) => {
    const section = sectionFor(official);
    section.officials.push(official);
    if (!section.districtLabel && official.districtLabel) {
      section.districtLabel = official.districtLabel;
    }
    if (!section.chamberName && official.chamberName) {
      section.chamberName = official.chamberName;
    }
    if (!section.jurisdictionName && official.jurisdictionName) {
      section.jurisdictionName = official.jurisdictionName;
    }
  });

  // Vacant seats have no officials to place them, so they are added directly.
  // Only district groups are ever reported unfilled -- a county does not report
  // an empty commission -- so these keep the district key shape.
  seats
    .filter((seat) => seat.vacant)
    .forEach((seat) => {
      const key = `${seat.group}:${seat.district || ''}`;
      if (sections.has(key)) return;
      sections.set(key, {
        key,
        group: seat.group,
        district: seat.district || '',
        districtLabel: seat.districtLabel,
        chamberName: seat.chamberName,
        jurisdictionName: null,
        officials: [],
        vacant: true,
        order: seat.order ?? 0,
      });
    });

  return [...sections.values()].sort(
    (a, b) =>
      (SEAT_GROUP_RANK[a.group] ?? 9) - (SEAT_GROUP_RANK[b.group] ?? 9) ||
      a.order - b.order ||
      String(a.district).localeCompare(String(b.district), undefined, { numeric: true })
  );
};

export { API2_BASE };